import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AIService from "./AIService";
import {
  ResearchReport,
  CreateResearchDTO,
  AppIdea,
  Competitor,
} from "../types/research";
import {
  AppResearchCitation,
  AppResearchInput,
  AppResearchReport,
} from "../types/appResearch";
import { CreateAppDTO } from "../types/app";

const RESEARCH_COLLECTION = "appResearch";
const APP_RESEARCH_REPORTS_COLLECTION = "appSpecificResearchReports";

interface AIResearchOutput {
  summary: string;
  trendingIdeas: AppIdea[];
  marketInsights: string[];
  competitorAnalysis: Competitor[];
  recommendations: string[];
  opportunityScore: number;
  riskFactors: string[];
  monetizationStrategies: string[];
}

interface AIAppResearchOutput {
  appName: string;
  appStoreUrl?: string;
  appLaunchDate?: string;
  category?: string;
  platforms: string[];
  rating?: string;
  downloadsEstimate?: string;
  whatItDoes: string;
  targetDemographics: string[];
  monetization: string[];
  keyFeatures: string[];
  growthSignals: string[];
  opportunities: string[];
  risks: string[];
  strategicRecommendations: string[];
  citations?: AppResearchCitation[];
  summary: string;
}

interface StoreMetadataLookup {
  platform: "android" | "ios" | "unknown";
  found: boolean;
  source?: string;
  query: string;
  storeUrl?: string | null;
  appId?: string | null;
  title?: string | null;
  description?: string | null;
  developer?: string | null;
  category?: string | null;
  rating?: string | number | null;
  ratingCount?: string | number | null;
  installs?: string | null;
  releaseDate?: string | null;
  updatedDate?: string | null;
  citation?: AppResearchCitation;
}

type AICloneOutput = {
  cloneName?: string;
  improvedDescription?: string;
};

const RESEARCH_OUTPUT_SCHEMA = {
  type: "object",
  properties: {
    summary: { type: "string" },
    trendingIdeas: {
      type: "array",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          description: { type: "string" },
          category: { type: "string" },
          targetAudience: { type: "string" },
          marketSize: { type: "string", enum: ["small", "medium", "large"] },
          competition: { type: "string", enum: ["low", "medium", "high"] },
          trendScore: { type: "number" },
          potentialRevenue: { type: "string" },
          validationNotes: { type: "string" },
        },
        required: [
          "title",
          "description",
          "category",
          "targetAudience",
          "marketSize",
          "competition",
          "trendScore",
          "potentialRevenue",
          "validationNotes",
        ],
      },
    },
    marketInsights: {
      type: "array",
      items: { type: "string" },
    },
    competitorAnalysis: {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: { type: "string" },
          category: { type: "string" },
          rating: { type: "number" },
          downloads: { type: "string" },
          keyFeatures: { type: "array", items: { type: "string" } },
          strengths: { type: "array", items: { type: "string" } },
          weaknesses: { type: "array", items: { type: "string" } },
          priceModel: { type: "string" },
        },
        required: [
          "name",
          "category",
          "rating",
          "downloads",
          "keyFeatures",
          "strengths",
          "weaknesses",
          "priceModel",
        ],
      },
    },
    recommendations: {
      type: "array",
      items: { type: "string" },
    },
    opportunityScore: { type: "number" },
    riskFactors: {
      type: "array",
      items: { type: "string" },
    },
    monetizationStrategies: {
      type: "array",
      items: { type: "string" },
    },
  },
  required: [
    "summary",
    "trendingIdeas",
    "marketInsights",
    "competitorAnalysis",
    "recommendations",
    "opportunityScore",
    "riskFactors",
    "monetizationStrategies",
  ],
};

const APP_RESEARCH_OUTPUT_SCHEMA = {
  type: "object",
  properties: {
    appName: { type: "string" },
    appStoreUrl: { type: "string" },
    appLaunchDate: { type: "string" },
    category: { type: "string" },
    platforms: { type: "array", items: { type: "string" } },
    rating: { type: "string" },
    downloadsEstimate: { type: "string" },
    whatItDoes: { type: "string" },
    targetDemographics: { type: "array", items: { type: "string" } },
    monetization: { type: "array", items: { type: "string" } },
    keyFeatures: { type: "array", items: { type: "string" } },
    growthSignals: { type: "array", items: { type: "string" } },
    opportunities: { type: "array", items: { type: "string" } },
    risks: { type: "array", items: { type: "string" } },
    strategicRecommendations: { type: "array", items: { type: "string" } },
    citations: {
      type: "array",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          url: { type: "string" },
          publisher: { type: "string" },
          publishedAt: { type: "string" },
          note: { type: "string" },
        },
        required: ["title", "url"],
      },
    },
    summary: { type: "string" },
  },
  required: [
    "appName",
    "platforms",
    "whatItDoes",
    "targetDemographics",
    "monetization",
    "keyFeatures",
    "growthSignals",
    "opportunities",
    "risks",
    "strategicRecommendations",
    "citations",
    "summary",
  ],
};

class ResearchService {
  /**
   * Generate a unique report ID
   */
  private generateReportId(): string {
    return `report_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  async conductAppResearch(
    userId: string,
    query: AppResearchInput,
    model?: string
  ): Promise<AppResearchReport> {
    const startTime = Date.now();
    const appQuery = query.appQuery?.trim();
    if (!appQuery) {
      throw new Error("App name or store URL is required");
    }

    try {
      let storeMetadata: StoreMetadataLookup | null = null;
      try {
        const metadata = await AIService.getStoreMetadata(appQuery);
        if (metadata?.found) {
          storeMetadata = metadata as StoreMetadataLookup;
        }
      } catch (lookupError) {
        console.warn("Store metadata lookup failed, continuing with search grounding.", lookupError);
      }

      const prompt = this.buildAppResearchPrompt(appQuery, storeMetadata);
      const aiResponse = await AIService.generateText(prompt, {
        model,
        useGoogleSearch: true,
        structuredOutputName: "app_research_report",
        structuredOutputSchema: APP_RESEARCH_OUTPUT_SCHEMA,
        temperature: 0.2,
        maxTokens: 4096,
      });

      const raw = (aiResponse.structuredOutput || {}) as Partial<AIAppResearchOutput>;
      const duration = (Date.now() - startTime) / 1000;
      const now = new Date();

      const citations = this.normalizeCitations(raw.citations);
      if (citations.length === 0 && storeMetadata?.citation?.url) {
        citations.push({
          title: String(storeMetadata.citation.title || storeMetadata.title || appQuery),
          url: String(storeMetadata.citation.url),
          publisher: this.normalizeOptionalString(storeMetadata.citation.publisher),
          publishedAt: this.normalizeOptionalString(storeMetadata.citation.publishedAt),
          note: this.normalizeOptionalString(storeMetadata.citation.note),
        });
      }

      const normalizedPlatforms = this.normalizeStringArray(raw.platforms).slice(0, 8);
      const fallbackPlatforms =
        storeMetadata?.platform && storeMetadata.platform !== "unknown"
          ? [storeMetadata.platform]
          : [];

      return {
        reportId: this.generateReportId(),
        userId,
        query: { appQuery },
        appName: String(raw.appName || storeMetadata?.title || appQuery),
        appStoreUrl: this.normalizeOptionalString(raw.appStoreUrl || storeMetadata?.storeUrl),
        appLaunchDate: this.normalizeOptionalString(raw.appLaunchDate || storeMetadata?.releaseDate),
        category: this.normalizeOptionalString(raw.category || storeMetadata?.category),
        platforms: normalizedPlatforms.length > 0 ? normalizedPlatforms : fallbackPlatforms,
        rating: this.normalizeOptionalString(raw.rating || String(storeMetadata?.rating || "")),
        downloadsEstimate: this.normalizeOptionalString(
          raw.downloadsEstimate || storeMetadata?.installs
        ),
        whatItDoes: String(
          raw.whatItDoes || storeMetadata?.description || "No description available."
        ).trim(),
        targetDemographics: this.normalizeStringArray(raw.targetDemographics),
        monetization: this.normalizeStringArray(raw.monetization),
        keyFeatures: this.normalizeStringArray(raw.keyFeatures),
        growthSignals: this.normalizeStringArray(raw.growthSignals),
        opportunities: this.normalizeStringArray(raw.opportunities),
        risks: this.normalizeStringArray(raw.risks),
        strategicRecommendations: this.normalizeStringArray(raw.strategicRecommendations),
        citations,
        summary: String(raw.summary || "Research completed successfully.").trim(),
        metadata: {
          generatedAt: now,
          aiModel: aiResponse.model || model || AIService.getDefaultModel(),
          researchDuration: duration,
        },
      };
    } catch (error) {
      console.error("Error conducting app research:", error);
      throw new Error("Failed to conduct app research");
    }
  }

  /**
   * Convert Firestore timestamp to Date
   */
  private convertTimestampToDate(timestamp: any): Date {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (timestamp?.toDate && typeof timestamp.toDate === "function") {
      return timestamp.toDate();
    }
    if (!timestamp) {
      return new Date();
    }
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) {
      return new Date();
    }
    return parsed;
  }

  /**
   * Conduct app idea research using Gemini
   */
  async conductResearch(
    userId: string,
    query: CreateResearchDTO,
    model?: string,
  ): Promise<ResearchReport> {
    const startTime = Date.now();

    try {
      const prompt = this.buildResearchPrompt(query);
      const aiResponse = await AIService.generateText(prompt, {
        model,
        useGoogleSearch: true,
        structuredOutputName: "app_idea_research_report",
        structuredOutputSchema: RESEARCH_OUTPUT_SCHEMA,
        temperature: 0.2,
        maxTokens: 4096,
      });
      const rawOutput = (aiResponse.structuredOutput ||
        {}) as Partial<AIResearchOutput>;

      const duration = (Date.now() - startTime) / 1000;
      const reportId = this.generateReportId();
      const now = new Date();

      return {
        reportId,
        userId,
        query,
        summary: rawOutput.summary || "Research completed successfully.",
        trendingIdeas: this.normalizeIdeas(
          rawOutput.trendingIdeas,
          query.category,
        ),
        marketInsights: this.normalizeStringArray(rawOutput.marketInsights),
        competitorAnalysis: this.normalizeCompetitors(
          rawOutput.competitorAnalysis,
        ),
        recommendations: this.normalizeStringArray(rawOutput.recommendations),
        opportunityScore: this.normalizeScore(rawOutput.opportunityScore),
        riskFactors: this.normalizeStringArray(rawOutput.riskFactors),
        monetizationStrategies: this.normalizeStringArray(
          rawOutput.monetizationStrategies,
        ),
        metadata: {
          generatedAt: now,
          aiModel: aiResponse.model || model || AIService.getDefaultModel(),
          researchDuration: duration,
        },
      };
    } catch (error) {
      console.error("Error conducting research:", error);
      throw new Error("Failed to conduct research");
    }
  }

  /**
   * Build research prompt for Gemini
   */
  private buildResearchPrompt(query: CreateResearchDTO): string {
    const parts = [
      `Conduct app idea research for the following:`,
      `Category: ${query.category}`,
      `Keywords: ${query.keywords.join(", ")}`,
      query.targetRegion ? `Target Region: ${query.targetRegion}` : "",
      `Time Range: ${query.timeRange}`,
      ``,
      `Provide insights on:`,
      `1. Trending app ideas in this space`,
      `2. Market opportunity analysis`,
      `3. Competitive landscape`,
      `4. Revenue potential`,
      `5. Risk factors`,
      `6. Monetization strategies`,
      `7. Target audience insights`,
    ];

    return parts.filter((p) => p).join("\n");
  }

  private normalizeStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => String(item).trim())
      .filter((item) => item.length > 0)
      .slice(0, 20);
  }

  private normalizeOptionalString(value: unknown): string | undefined {
    const normalized = String(value || "").trim();
    return normalized ? normalized : undefined;
  }

  private buildAppResearchPrompt(
    appQuery: string,
    storeMetadata?: StoreMetadataLookup | null
  ): string {
    const metadataBlock = storeMetadata?.found
      ? [
          "",
          "Verified store metadata:",
          `- Platform: ${storeMetadata.platform}`,
          `- Store URL: ${storeMetadata.storeUrl || "N/A"}`,
          `- App ID: ${storeMetadata.appId || "N/A"}`,
          `- Title: ${storeMetadata.title || "N/A"}`,
          `- Developer: ${storeMetadata.developer || "N/A"}`,
          `- Category: ${storeMetadata.category || "N/A"}`,
          `- Rating: ${storeMetadata.rating || "N/A"}`,
          `- Rating count: ${storeMetadata.ratingCount || "N/A"}`,
          `- Installs: ${storeMetadata.installs || "N/A"}`,
          `- Release date: ${storeMetadata.releaseDate || "N/A"}`,
          `- Last updated: ${storeMetadata.updatedDate || "N/A"}`,
          `- Description: ${storeMetadata.description || "N/A"}`,
          "",
          "Use the verified metadata above as baseline facts even if web search results are sparse.",
        ].join("\n")
      : "";

    return [
      "Research this specific mobile app with web-grounded evidence.",
      `App identifier (name or store URL): ${appQuery}`,
      metadataBlock,
      "",
      "Return practical insights covering:",
      "1. what the app does",
      "2. target demographics/users",
      "3. monetization model",
      "4. app launch date",
      "5. category, platforms, ratings/download estimates (if available)",
      "6. key features and growth signals",
      "7. opportunities, risks, and strategic recommendations",
      "8. source citations with direct URLs",
      "",
      "Use concise bullet-style facts and avoid speculation when data is unavailable.",
      "Include citation entries for the main sources used from search results.",
    ].join("\n");
  }

  private normalizeCitations(value: unknown): AppResearchCitation[] {
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && typeof item === "object")
      .map((item: any) => ({
        title: String(item.title || "").trim(),
        url: String(item.url || "").trim(),
        publisher: this.normalizeOptionalString(item.publisher),
        publishedAt: this.normalizeOptionalString(item.publishedAt),
        note: this.normalizeOptionalString(item.note),
      }))
      .filter((item) => item.title.length > 0 && /^https?:\/\//i.test(item.url))
      .slice(0, 20);
  }

  private removeUndefinedDeep<T>(value: T): T {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.removeUndefinedDeep(item))
        .filter((item) => item !== undefined) as T;
    }

    if (value && typeof value === "object") {
      const cleaned: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
        if (val === undefined) return;
        cleaned[key] = this.removeUndefinedDeep(val);
      });
      return cleaned as T;
    }

    return value;
  }

  private normalizeScore(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, Math.round(parsed)));
  }

  private normalizeIdeas(value: unknown, fallbackCategory: string): AppIdea[] {
    if (!Array.isArray(value)) return [];

    return value
      .filter((item) => typeof item === "object" && item !== null)
      .map((item: any) => ({
        title: String(item.title || "Untitled Idea"),
        description: String(item.description || ""),
        category: String(item.category || fallbackCategory),
        targetAudience: String(item.targetAudience || "General users"),
        marketSize: this.normalizeMarketSize(item.marketSize),
        competition: this.normalizeCompetition(item.competition),
        trendScore: this.normalizeScore(item.trendScore),
        potentialRevenue: String(item.potentialRevenue || "Unknown"),
        validationNotes: String(item.validationNotes || ""),
      }))
      .slice(0, 12);
  }

  private normalizeCompetitors(value: unknown): Competitor[] {
    if (!Array.isArray(value)) return [];

    return value
      .filter((item) => typeof item === "object" && item !== null)
      .map((item: any) => ({
        name: String(item.name || "Unknown Competitor"),
        category: String(item.category || "Unknown"),
        rating: this.normalizeRating(item.rating),
        downloads: String(item.downloads || "N/A"),
        keyFeatures: this.normalizeStringArray(item.keyFeatures),
        strengths: this.normalizeStringArray(item.strengths),
        weaknesses: this.normalizeStringArray(item.weaknesses),
        priceModel: String(item.priceModel || "Unknown"),
      }))
      .slice(0, 15);
  }

  private normalizeMarketSize(value: unknown): "small" | "medium" | "large" {
    const normalized = String(value || "").toLowerCase();
    if (
      normalized === "small" ||
      normalized === "medium" ||
      normalized === "large"
    ) {
      return normalized;
    }
    return "medium";
  }

  private normalizeCompetition(value: unknown): "low" | "medium" | "high" {
    const normalized = String(value || "").toLowerCase();
    if (
      normalized === "low" ||
      normalized === "medium" ||
      normalized === "high"
    ) {
      return normalized;
    }
    return "medium";
  }

  private normalizeRating(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(5, Math.round(parsed * 10) / 10));
  }

  /**
   * Save research report to Firestore
   */
  async saveReport(report: ResearchReport): Promise<string> {
    try {
      const { targetRegion, ...queryWithoutTargetRegion } = report.query;
      const sanitizedQuery = {
        ...queryWithoutTargetRegion,
        ...(targetRegion && targetRegion.trim()
          ? { targetRegion: targetRegion.trim() }
          : {}),
      };

      const reportData = {
        ...report,
        query: sanitizedQuery,
        metadata: {
          ...report.metadata,
          generatedAt: serverTimestamp(),
        },
        savedAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        collection(db, RESEARCH_COLLECTION),
        reportData,
      );
      return docRef.id;
    } catch (error) {
      console.error("Error saving report:", error);
      throw new Error("Failed to save research report");
    }
  }

  /**
   * Get all research reports for a user
   */
  async getResearchReports(userId: string): Promise<ResearchReport[]> {
    try {
      const reportsRef = collection(db, RESEARCH_COLLECTION);
      const q = query(reportsRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          reportId: data.reportId,
          userId: data.userId,
          query: data.query,
          summary: data.summary,
          trendingIdeas: data.trendingIdeas,
          marketInsights: data.marketInsights,
          competitorAnalysis: data.competitorAnalysis,
          recommendations: data.recommendations,
          opportunityScore: data.opportunityScore,
          riskFactors: data.riskFactors,
          monetizationStrategies: data.monetizationStrategies,
          metadata: {
            generatedAt: this.convertTimestampToDate(
              data.metadata?.generatedAt,
            ),
            aiModel: data.metadata?.aiModel,
            researchDuration: data.metadata?.researchDuration || 0,
          },
          savedAt: data.savedAt
            ? this.convertTimestampToDate(data.savedAt)
            : undefined,
        } as ResearchReport;
      });
    } catch (error) {
      console.error("Error fetching research reports:", error);
      throw new Error("Failed to fetch research reports");
    }
  }

  /**
   * Delete a research report
   */
  async deleteReport(reportId: string, userId: string): Promise<void> {
    try {
      const reportsRef = collection(db, RESEARCH_COLLECTION);
      const q = query(
        reportsRef,
        where("reportId", "==", reportId),
        where("userId", "==", userId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Report not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting report:", error);
      throw new Error("Failed to delete research report");
    }
  }

  async saveAppResearchReport(report: AppResearchReport): Promise<string> {
    try {
      const reportData = this.removeUndefinedDeep({
        ...report,
        metadata: {
          ...report.metadata,
          generatedAt: serverTimestamp(),
        },
        savedAt: serverTimestamp(),
      });

      const docRef = await addDoc(
        collection(db, APP_RESEARCH_REPORTS_COLLECTION),
        reportData
      );
      return docRef.id;
    } catch (error) {
      console.error("Error saving app research report:", error);
      throw new Error("Failed to save app research report");
    }
  }

  async getAppResearchReports(userId: string): Promise<AppResearchReport[]> {
    try {
      const reportsRef = collection(db, APP_RESEARCH_REPORTS_COLLECTION);
      const q = query(reportsRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          reportId: data.reportId,
          userId: data.userId,
          query: data.query,
          appName: data.appName,
          appStoreUrl: data.appStoreUrl,
          appLaunchDate: data.appLaunchDate,
          category: data.category,
          platforms: this.normalizeStringArray(data.platforms),
          rating: data.rating,
          downloadsEstimate: data.downloadsEstimate,
          whatItDoes: data.whatItDoes,
          targetDemographics: this.normalizeStringArray(data.targetDemographics),
          monetization: this.normalizeStringArray(data.monetization),
          keyFeatures: this.normalizeStringArray(data.keyFeatures),
          growthSignals: this.normalizeStringArray(data.growthSignals),
          opportunities: this.normalizeStringArray(data.opportunities),
          risks: this.normalizeStringArray(data.risks),
          strategicRecommendations: this.normalizeStringArray(data.strategicRecommendations),
          citations: this.normalizeCitations(data.citations),
          summary: data.summary,
          metadata: {
            generatedAt: this.convertTimestampToDate(data.metadata?.generatedAt),
            aiModel: data.metadata?.aiModel || "",
            researchDuration: data.metadata?.researchDuration || 0,
          },
          savedAt: data.savedAt ? this.convertTimestampToDate(data.savedAt) : undefined,
        } as AppResearchReport;
      });
    } catch (error) {
      console.error("Error fetching app research reports:", error);
      throw new Error("Failed to fetch app research reports");
    }
  }

  async deleteAppResearchReport(reportId: string, userId: string): Promise<void> {
    try {
      const reportsRef = collection(db, APP_RESEARCH_REPORTS_COLLECTION);
      const q = query(
        reportsRef,
        where("reportId", "==", reportId),
        where("userId", "==", userId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Report not found");
      }

      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting app research report:", error);
      throw new Error("Failed to delete app research report");
    }
  }

  /**
   * Export report as JSON
   */
  exportReportAsJSON(report: ResearchReport): string {
    return JSON.stringify(report, null, 2);
  }

  /**
   * Generate CSV from trending ideas
   */
  exportIdeasAsCSV(ideas: AppIdea[]): string {
    const headers = [
      "Title",
      "Description",
      "Category",
      "Target Audience",
      "Market Size",
      "Competition",
      "Trend Score",
      "Potential Revenue",
    ];

    const rows = ideas.map((idea) => [
      idea.title,
      idea.description,
      idea.category,
      idea.targetAudience,
      idea.marketSize,
      idea.competition,
      idea.trendScore,
      idea.potentialRevenue,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    return csv;
  }

  async buildCloneRegistrationPrefill(
    report: AppResearchReport,
    basePrefill: CreateAppDTO,
    model?: string,
    options?: {
      excludeNames?: string[];
    }
  ): Promise<CreateAppDTO> {
    const excludeNames = (options?.excludeNames || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean);

    const prompt = [
      "You are an expert app product strategist.",
      "Create an alternative clone concept for this app by improving only the app name and description.",
      "Keep the same app type, category, and target user intent.",
      "",
      "Source app context:",
      `- Current app name: ${report.appName}`,
      `- Category: ${report.category || basePrefill.category}`,
      `- Existing description: ${basePrefill.description || report.whatItDoes || report.summary}`,
      `- What app does: ${report.whatItDoes || "N/A"}`,
      `- Key features: ${(report.keyFeatures || []).join(", ") || "N/A"}`,
      `- Target demographics: ${(report.targetDemographics || []).join(", ") || "N/A"}`,
      `- Monetization: ${(report.monetization || []).join(", ") || "N/A"}`,
      "",
      "Requirements:",
      "- Return a completely new brand identity name (not too long) with similarities to the original app name related to its core functionality or theme.",
      "- Ensure the new name adds a core feature of the original app concept at the end of the name to make it feel like an improved version of the original app and have the lmain keyword the original app has.",
      "- Name priority order: (1) uniqueness first, (2) then subtle similarity to the original app concept.",
      "- The new name should feel distinctive and ownable, while still contextually relevant to the same app category/use-case.",
      "- Do not reuse or closely mirror the current app brand name.",
      "- Description must use the new brand name explicitly.",
      "- Remove/avoid any wording that sounds like the old brand identity.",
      "- Return a concise and clear improved app description tailored to this app.",
      excludeNames.length > 0
        ? `- Never return any of these previously used clone names: ${excludeNames.join(", ")}`
        : "",
      "- Do not change app category, platform, store links, IDs, or research context.",
      "- Return JSON only with: { cloneName: string, improvedDescription: string }",
    ].join("\n");

    const schema = {
      type: "object",
      properties: {
        cloneName: { type: "string" },
        improvedDescription: { type: "string" },
      },
      required: ["cloneName", "improvedDescription"],
    };

    try {
      const primaryResponse = await AIService.generateText(prompt, {
        provider: "openai",
        model: "gpt-4.1",
        structuredOutputName: "app_clone_prefill",
        structuredOutputSchema: schema,
        temperature: 0.4,
        maxTokens: 512,
      });

      let result =
        ((primaryResponse.structuredOutput as AICloneOutput | undefined) ?? null) ||
        this.parseLooseJson<AICloneOutput>(primaryResponse.text) ||
        {};

      console.log("[CloneDebug] Primary response", {
        model: primaryResponse.model,
        provider: primaryResponse.provider,
        hasStructuredOutput:
          primaryResponse.structuredOutput !== undefined &&
          primaryResponse.structuredOutput !== null,
        structuredOutput: primaryResponse.structuredOutput,
        textLength: (primaryResponse.text || "").length,
        textPreview: (primaryResponse.text || "").slice(0, 400),
      });

      if (this.isInvalidCloneOutput(result, report.appName)) {
        const fallbackPrompt = [
          prompt,
          "",
          "Important:",
          "- Return strict JSON only.",
          "- Do not return empty strings.",
          "- If uncertain, still provide your best non-empty cloneName and improvedDescription.",
          '{ "cloneName": "...", "improvedDescription": "..." }',
        ].join("\n");
        const fallbackText = await AIService.generateText(
          fallbackPrompt,
          {
            provider: "openai",
            model: "gpt-4.1",
            temperature: 0.4,
            maxTokens: 512,
          }
        );
        const parsedFallback = this.parseLooseJson<AICloneOutput>(fallbackText.text);

        console.log("[CloneDebug] Fallback response", {
          model: fallbackText.model,
          provider: fallbackText.provider,
          textLength: (fallbackText.text || "").length,
          textPreview: (fallbackText.text || "").slice(0, 400),
          parsedFallback,
        });

        if (parsedFallback) {
          result = parsedFallback;
        }
      }

      if (this.isInvalidCloneOutput(result, report.appName)) {
        const crossProviderPrompt = [
          prompt,
          "",
          "Return ONLY JSON with non-empty values.",
          '{ "cloneName": "new unique brand name", "improvedDescription": "description that includes the cloneName" }',
        ].join("\n");
        const crossProviderResponse = await AIService.generateText(crossProviderPrompt, {
          provider: "openai",
          model: "gpt-4.1",
          temperature: 0.5,
          maxTokens: 512,
        });
        const crossParsed =
          ((crossProviderResponse.structuredOutput as AICloneOutput | undefined) ?? null) ||
          this.parseLooseJson<AICloneOutput>(crossProviderResponse.text);

        console.log("[CloneDebug] Cross-provider fallback response", {
          model: crossProviderResponse.model,
          provider: crossProviderResponse.provider,
          textLength: (crossProviderResponse.text || "").length,
          textPreview: (crossProviderResponse.text || "").slice(0, 400),
          parsed: crossParsed,
        });

        if (crossParsed) {
          result = crossParsed;
        }
      }

      let cloneName = String(result?.cloneName || "").trim();
      let improvedDescription = String(result?.improvedDescription || "").trim();
      const sourceName = String(report.appName || "").trim();
      const excludeSet = new Set(
        [sourceName, ...excludeNames]
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean)
      );

      if (!cloneName) {
        cloneName = this.buildFallbackCloneName(basePrefill.name, report.category, excludeSet);
      }
      if (!improvedDescription) {
        improvedDescription = `${cloneName} is a refined app experience focused on ${
          report.whatItDoes || basePrefill.description || report.summary
        }`;
      }

      if (cloneName && sourceName) {
        const escapedSource = sourceName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        improvedDescription = improvedDescription.replace(
          new RegExp(escapedSource, "gi"),
          cloneName
        );
      }
      if (excludeSet.has(cloneName.toLowerCase())) {
        cloneName = this.buildFallbackCloneName(cloneName, report.category, excludeSet);
      }
      if (cloneName && improvedDescription && !improvedDescription.includes(cloneName)) {
        improvedDescription = `${cloneName} — ${improvedDescription}`;
      }

      console.log("[CloneDebug] Final clone output", {
        sourceAppName: report.appName,
        cloneName,
        improvedDescription,
      });

      return {
        ...basePrefill,
        name: cloneName,
        description: improvedDescription,
        storeId: "",
        storeUrl: "",
        androidStoreUrl: "",
        iosStoreUrl: "",
      };
    } catch (error) {
      console.warn("Falling back to local clone prefill defaults:", error);
      const fallbackName = this.buildFallbackCloneName(
        basePrefill.name,
        report.category,
        new Set(
          [report.appName, ...(options?.excludeNames || [])]
            .map((item) => String(item || "").trim().toLowerCase())
            .filter(Boolean)
        )
      );
      return {
        ...basePrefill,
        name: fallbackName,
        description: `${fallbackName} is a refreshed app concept focused on ${
          report.whatItDoes || basePrefill.description || report.summary
        }`,
        storeId: "",
        storeUrl: "",
        androidStoreUrl: "",
        iosStoreUrl: "",
      };
    }
  }

  private isInvalidCloneOutput(output: AICloneOutput | null | undefined, sourceName: string): boolean {
    const name = String(output?.cloneName || "").trim();
    const description = String(output?.improvedDescription || "").trim();
    if (!name || !description) return true;
    if (name.length < 2 || description.length < 10) return true;
    if (sourceName && name.toLowerCase() === sourceName.trim().toLowerCase()) return true;
    return false;
  }

  private parseLooseJson<T>(text: string): T | null {
    const raw = String(text || "").trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      // continue
    }

    const fenced =
      raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced) as T;
      } catch {
        // continue
      }
    }

    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(raw.slice(start, end + 1)) as T;
      } catch {
        return null;
      }
    }

    return null;
  }

  private buildFallbackCloneName(
    baseName: string,
    category?: string,
    excludeSet: Set<string> = new Set<string>()
  ): string {
    const cleanBase = String(baseName || "App")
      .replace(/[^a-zA-Z0-9 ]+/g, " ")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .join(" ");
    const categoryHint = String(category || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 6);
    const suffixes = ["Nova", "Pulse", "Flow", "Forge", "Spark", "Lift", "Edge"];
    const preferred = `${cleanBase || "App"} ${categoryHint ? `${categoryHint} ` : ""}${suffixes[Math.floor(Math.random() * suffixes.length)]}`.trim();
    if (!excludeSet.has(preferred.toLowerCase())) {
      return preferred;
    }

    for (const suffix of suffixes) {
      const candidate = `${cleanBase || "App"} ${categoryHint ? `${categoryHint} ` : ""}${suffix}`.trim();
      if (!excludeSet.has(candidate.toLowerCase())) {
        return candidate;
      }
    }

    return `${cleanBase || "App"} ${categoryHint ? `${categoryHint} ` : ""}${Date.now().toString().slice(-4)}`.trim();
  }
}

const instance = new ResearchService();
export default instance;
